import type { LayoutStyle } from '@layout/models/layout.enum';
import type { LocalesUnion } from '@constants/i18n';
import type { ComponentsToInject } from '@utils/mdx/components';
import type { SerializedMDX } from '@utils/mdx/serialization';

export type DocumentHeroColor =
  | 'blue'
  | 'lime'
  | 'yellow'
  | 'red'
  | 'pink'
  | 'purple'
  | 'indigo'
  | 'cyan'
  | 'green'
  | 'amber'
  | 'orange'
  | 'brown'
  | 'violet';

export enum PagePosInNavStructure {
  ROOT = 'root',
}

export enum TimeToReadMode {
  DISABLED = 'disabled',
}

export type TimeToRead = number | TimeToReadMode;

export interface BaseSchema {
  /** File path relative to `contentDirPath` */
  _id: string;
  _raw: {
    /** Relative to `contentDirPath` */
    sourceFilePath: string;
    sourceFileName: string;
    /** Relative to `contentDirPath` */
    sourceFileDir: string;
    contentType: 'markdown' | 'mdx' | 'data';
    /** A path e.g. useful as URL paths based on `sourceFilePath` with file extension removed and `/index` removed. */
    flattenedPath: string;
  };
  type: 'DocumentSchema';
  /** Markdown file body */
  body: {
    /** Raw Markdown source */
    raw: string;
    html?: string;
  };
  /** Serialized markdown file body */
  serialized?: SerializedMDX;
  locale: LocalesUnion;
}

export interface Document {
  id?: string;
  tags?: string[];
  heroLabels?: string[];
  navTitle?: string;
  heroTitle?: string;
  description?: string;
  heroColor?: DocumentHeroColor;
  inject?: Array<keyof ComponentsToInject>;
  layout?: LayoutStyle;
  timeToRead?: TimeToRead;
  seoTitle?: string;
  seoDescription?: string;
  seoNoIndex?: boolean;
  seoNoFollow?: boolean;
  navStructure?: PagePosInNavStructure;
  status?: 'draft' | null;
  protectedGroups?: {
    meta: {
      instructionId: string;
    };
    permissions: string[];
    flags: string[];
  };
}

export type DocumentSchema = BaseSchema & Document;
export type ReducedDocumentSchema = { _id: string } & Partial<DocumentSchema>;
export type DocumentProjection = (keyof DocumentSchema)[];
